import { Button } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { type AnyJobListColumnId } from '@/features/jobs/list/job-list-columns'
import styles from './ExportJobsDialog.module.css'

export interface ExportColumnOption {
  id: AnyJobListColumnId
  header: string
  isSelected: boolean
}

export interface ExportJobsDialogProps {
  showing: boolean
  onClose: () => void
  totalJobs: number
  savedFilterName: string | null
  shownColumns: ExportColumnOption[]
  moreColumns: ExportColumnOption[]
  selectedCount: number
  isExportPending: boolean
  onExport: () => void
  onToggleColumn: (id: AnyJobListColumnId) => void
  onSetShownColumns: (selected: boolean) => void
  onSetMoreColumns: (selected: boolean) => void
}

export function ExportJobsDialog({
  showing,
  onClose,
  totalJobs: total,
  savedFilterName,
  shownColumns,
  moreColumns,
  selectedCount,
  isExportPending,
  onExport,
  onToggleColumn,
  onSetShownColumns,
  onSetMoreColumns,
}: ExportJobsDialogProps) {
  const jobs = `${total} ${total === 1 ? 'job' : 'jobs'}`

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isExportPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Export jobs</ModalSheetTitle>
        <ModalSheetHeaderDescription>
          {jobs}
          {savedFilterName !== null && ` from ${savedFilterName}`}, with your
          current search and filters. Pick the columns to include.
        </ModalSheetHeaderDescription>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <div className={styles.body}>
            <ColumnGroup
              title="In your table"
              columns={shownColumns}
              onToggleColumn={onToggleColumn}
              onSetAll={onSetShownColumns}
            />
            <ColumnGroup
              title="More columns"
              columns={moreColumns}
              onToggleColumn={onToggleColumn}
              onSetAll={onSetMoreColumns}
            />
          </div>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer>
          <span className={styles.summary}>
            {selectedCount} {selectedCount === 1 ? 'column' : 'columns'}
          </span>
        </ModalSheetFooterSpacer>
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            onPress={onClose}
            isDisabled={isExportPending}
          />
          <Button
            label={`Export ${jobs}`}
            variant="primary"
            onPress={onExport}
            isPending={isExportPending}
            isDisabled={selectedCount === 0}
            testId="job-export-submit"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface ColumnGroupProps {
  title: string
  columns: ExportColumnOption[]
  onToggleColumn: (id: AnyJobListColumnId) => void
  onSetAll: (selected: boolean) => void
}

function ColumnGroup({
  title,
  columns,
  onToggleColumn,
  onSetAll,
}: ColumnGroupProps) {
  if (!columns.length) {
    return null
  }
  const allSelected = columns.every((c) => c.isSelected)

  return (
    <section>
      <div className={styles.groupHeader}>
        <span>{title}</span>
        <Button
          label={allSelected ? 'Clear' : 'Select all'}
          variant="text"
          size="small"
          onPress={() => onSetAll(!allSelected)}
        />
      </div>
      <div className={styles.columns}>
        {columns.map((column) => (
          <label key={column.id} className={styles.column}>
            <Checkbox
              size={20}
              checked={column.isSelected}
              onChange={() => onToggleColumn(column.id)}
              testId={`job-export-column-${column.id}`}
            />
            <span className={styles.columnLabel}>{column.header}</span>
          </label>
        ))}
      </div>
    </section>
  )
}
